import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWishlist } from '../../../hooks/useWishlist';

export default function Navbar() {
  const navigate = useNavigate();
  const [showAdmin, setShowAdmin] = useState(false);
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const { wishlistCount } = useWishlist();

  const ADMIN_PASSCODE = '2468';

  const handleAdminClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPasscodeModal(true);
    setPasscode('');
    setError('');
  };

  const handlePasscodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passcode === ADMIN_PASSCODE) {
      setShowPasscodeModal(false);
      navigate('/admin');
    } else {
      setError('Incorrect passcode. Please try again.');
      setPasscode('');
    }
  };

  const handleModalClose = () => {
    setShowPasscodeModal(false);
    setPasscode('');
    setError('');
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img 
              src="https://static.readdy.ai/image/e5292f43a8fca29a2b9f75d3d42cfb52/a79cb0bec034472cb10b80478a2788a7.png" 
              alt="iCarga Logo" 
              className="h-16 w-16 rounded-full object-cover"
            />
            <span className="text-3xl font-bold text-gray-900">
              iCarGA
            </span>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <a 
              onClick={() => navigate('/inventory')}
              className="text-sm font-light text-gray-700 hover:text-[#2563EB] transition-colors cursor-pointer"
            >
              Inventory
            </a>
            <a 
              onClick={() => navigate('/financing')}
              className="text-sm font-light text-[#2563EB] transition-colors cursor-pointer"
            >
              Financing
            </a>
            <a 
              onClick={() => navigate('/sell-trade')}
              className="text-sm font-light text-gray-700 hover:text-[#2563EB] transition-colors cursor-pointer"
            >
              Sell/Trade
            </a>
            <a 
              onClick={() => navigate('/reviews')}
              className="text-sm font-light text-gray-700 hover:text-[#2563EB] transition-colors cursor-pointer"
            >
              Reviews
            </a>
            {showAdmin && (
              <a 
                href="/admin" 
                onClick={handleAdminClick}
                className="text-sm font-light text-red-600 hover:text-red-700 transition-colors cursor-pointer"
              >
                <i className="ri-admin-line mr-1"></i>Admin
              </a>
            )}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/wishlist')}
              className="relative w-10 h-10 flex items-center justify-center text-gray-700 hover:text-red-500 transition-colors cursor-pointer"
              title="View Wishlist"
            >
              <i className={`text-2xl ${wishlistCount > 0 ? 'ri-heart-fill text-red-500' : 'ri-heart-line'}`}></i>
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>

            <button 
              onClick={() => navigate('/financing?apply=true')}
              className="px-6 py-2.5 bg-[#2563EB] text-white rounded-full text-sm font-medium hover:bg-[#1d4ed8] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Pre-Approved
            </button>
          </div>
        </div>
      </nav>

      {/* Passcode Modal */}
      {showPasscodeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-[fadeIn_0.3s_ease-out]">
            <button
              onClick={handleModalClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-password-line text-3xl text-red-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin Access</h2>
              <p className="text-sm text-gray-600">Enter the passcode to continue</p>
            </div>

            <form onSubmit={handlePasscodeSubmit}>
              <div className="mb-6">
                <input
                  type="password"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="Enter passcode"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-center text-2xl tracking-widest focus:border-red-500 focus:outline-none transition-colors"
                  maxLength={6}
                  autoFocus
                />
                {error && (
                  <p className="text-red-600 text-sm mt-2 flex items-center justify-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {error}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Access Admin Panel
              </button>
            </form>

            <p className="text-xs text-gray-500 text-center mt-4">
              This area is restricted to authorized personnel only
            </p>
          </div>
        </div>
      )}
    </>
  );
}
